import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import MoleculeLoader from '../components/MoleculeLoader';
import { toast } from 'sonner';
import { CheckCircle2, Clock, XCircle, Package, Download } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CustomOrderPage = () => {
  const { token } = useParams();
  const [status, setStatus] = React.useState('loading');
  const [quote, setQuote] = React.useState(null);
  const [settings, setSettings] = React.useState(null);
  const [method, setMethod] = React.useState('');
  const [submitting, setSubmitting] = React.useState(false);
  const [reserved, setReserved] = React.useState(null);
  const [addr, setAddr] = React.useState({ name: '', address_line1: '', address_line2: '', city: '', state: '', zip_code: '' });
  const [phone, setPhone] = React.useState('');
  const [zelleContact, setZelleContact] = React.useState({ name: '', handle: '' });

  React.useEffect(() => {
    fetch(`${API_URL}/api/site-settings`).then((r) => (r.ok ? r.json() : null)).then((s) => s && setSettings(s)).catch(() => {});
    fetch(`${API_URL}/api/custom-orders/${token}`)
      .then((r) => { if (!r.ok) throw new Error('not found'); return r.json(); })
      .then((d) => {
        setQuote(d);
        setAddr((a) => ({ ...a, name: d.recipient_name || '' }));
        if (d.status === 'reserved') {
          setReserved({ order_id: d.reserved_order_id, total: d.total, payment_method: d.reserved_payment_method });
          setStatus('reserved');
        } else {
          const opts = d.payment_options || {};
          setMethod(opts.zelle_enabled ? 'zelle' : (opts.cashapp_enabled ? 'cash_app' : ''));
          setStatus('ready');
        }
      })
      .catch(() => setStatus('error'));
  }, [token]);

  const submit = async () => {
    if (!method) { toast.error('Choose a payment method'); return; }
    if (!addr.address_line1.trim() || !addr.city.trim() || !addr.state.trim() || !addr.zip_code.trim()) {
      toast.error('Please complete your shipping address'); return;
    }
    if (phone.replace(/\D/g, '').length < 10) { toast.error('Enter a valid phone number'); return; }
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/custom-orders/${token}/reserve`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          payment_method: method,
          shipping_address: { ...addr, name: addr.name || quote.recipient_name },
          phone,
          zelle_contact: method === 'zelle' ? zelleContact : undefined,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Reservation failed');
      setReserved(data);
      setStatus('reserved');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (e) { toast.error(e.message); }
    setSubmitting(false);
  };

  if (status === 'loading') {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <MoleculeLoader size={96} label="Loading your custom order…" />
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
        <Card className="w-full max-w-md"><CardContent className="p-8 text-center">
          <div className="w-16 h-16 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-6"><XCircle className="h-8 w-8 text-amber-600" /></div>
          <h1 className="font-heading text-xl font-bold text-slate-900 mb-2">Link Unavailable</h1>
          <p className="text-slate-600 mb-6">This custom order link is invalid or has expired. Please contact us if you believe this is a mistake.</p>
          <Link to="/contact"><Button className="bg-teal-700 hover:bg-teal-800 text-white">Contact Support</Button></Link>
        </CardContent></Card>
      </div>
    );
  }

  // Reserved / payment instructions
  if (status === 'reserved' && reserved) {
    const isZelle = reserved.payment_method === 'zelle';
    const payHandle = isZelle ? (settings?.zelle_handle || '') : (settings?.cashapp_handle || '');
    const payQrUrl = isZelle ? (settings?.zelle_qr_url || '') : (settings?.cashapp_qr_url || '');
    const payNote = isZelle ? (settings?.zelle_note || '') : (settings?.cashapp_note || '');
    const payRecipient = isZelle ? (settings?.zelle_recipient_name || '') : '';
    const payLabel = isZelle ? 'Zelle' : 'Cash App';
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center py-16 px-4">
        <Card className="w-full max-w-lg" data-testid="custom-order-reserved">
          <CardContent className="p-8 text-center">
            <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6 bg-amber-100"><Clock className="h-10 w-10 text-amber-600" /></div>
            <h1 className="font-heading text-2xl font-bold text-slate-900 mb-2">Order Reserved — One More Step</h1>
            <p className="text-slate-600 mb-8">Your custom order is reserved and we've been notified. To complete it, send your {payLabel} payment using the details below.</p>

            <div className="border-2 border-amber-300 rounded-xl p-4 mb-6 text-left bg-amber-50/50" data-testid="custom-manual-pay">
              <h2 className="font-heading font-semibold text-slate-900 mb-3 text-center">Send your {payLabel} payment</h2>
              {payQrUrl && (
                <div className="flex flex-col items-center bg-white rounded-lg p-4 mb-3">
                  <img src={`${API_URL}${payQrUrl}`} alt={`${payLabel} QR code`} className="w-44 h-44 object-contain rounded-lg bg-white" />
                  <span className="mt-2 text-xs text-slate-500 text-center">Open your {isZelle ? 'bank' : 'Cash App'} app and scan this QR{payHandle ? ' — or use the handle below' : ''}.</span>
                </div>
              )}
              {payHandle && (
                <div className="rounded-lg bg-white border border-slate-200 p-3 mb-3">
                  <p className="text-xs text-slate-500">Send to</p>
                  <p className="font-mono font-semibold text-purple-700 break-all">{payHandle}</p>
                  {payRecipient && <p className="text-xs text-slate-500 mt-1">{payRecipient}</p>}
                </div>
              )}
              <div className="grid grid-cols-2 gap-3 mb-3">
                <div className="rounded-lg bg-white border border-slate-200 p-3">
                  <p className="text-xs text-slate-500">Exact amount</p>
                  <p className="font-bold text-purple-700 text-lg" data-testid="custom-pay-amount">${Number(reserved.total || 0).toFixed(2)}</p>
                </div>
                <div className="rounded-lg bg-white border border-slate-200 p-3">
                  <p className="text-xs text-slate-500">Include this memo</p>
                  <p className="font-mono font-semibold text-amber-900 break-all" data-testid="custom-pay-memo">{reserved.order_id}</p>
                </div>
              </div>
              {payNote && <p className="text-xs text-slate-600">{payNote}</p>}
              <p className="text-xs text-amber-800 mt-2 font-medium">Once we receive your payment, we'll confirm your order and email your tracking.</p>
            </div>

            <div className="bg-teal-50 rounded-lg p-4 mb-6 flex items-center justify-center gap-3">
              <Package className="h-5 w-5 text-teal-700" />
              <span className="text-sm text-teal-800 font-medium">A confirmation email has been sent</span>
            </div>
            <Link to="/"><Button variant="outline" className="w-full sm:w-auto">Back to Home</Button></Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Quote + reserve form
  const opts = quote.payment_options || {};
  return (
    <div className="min-h-screen bg-slate-50 py-12 px-4">
      <div className="max-w-2xl mx-auto space-y-6" data-testid="custom-order-page">
        <div className="text-center">
          <h1 className="font-heading text-3xl font-bold text-slate-900">Your Custom Order</h1>
          <p className="text-slate-500 mt-1">Prepared for {quote.recipient_name}</p>
        </div>

        <Card>
          <CardContent className="p-6">
            {quote.note && <p className="mb-4 p-3 bg-slate-50 rounded-lg text-sm text-slate-600">{quote.note}</p>}
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-slate-500 border-b">
                  <th className="py-2">Item</th><th className="py-2 text-center">Qty</th><th className="py-2 text-right">Unit</th><th className="py-2 text-right">Total</th>
                </tr>
              </thead>
              <tbody>
                {(quote.items || []).map((it, i) => (
                  <tr key={i} className="border-b last:border-0">
                    <td className="py-2">{it.name}{it.vial_size ? <span className="text-slate-400 text-xs block">{it.vial_size}</span> : null}</td>
                    <td className="py-2 text-center">{it.quantity}</td>
                    <td className="py-2 text-right">${Number(it.unit_price).toFixed(2)}</td>
                    <td className="py-2 text-right font-medium">${Number(it.line_total).toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="mt-4 space-y-1 text-sm text-right">
              <div className="text-slate-600">Subtotal: ${Number(quote.subtotal).toFixed(2)}</div>
              {quote.discount_total > 0 && <div className="text-teal-700">Discount: −${Number(quote.discount_total).toFixed(2)}</div>}
              {quote.shipping_cost > 0 && <div className="text-slate-600">Shipping: ${Number(quote.shipping_cost).toFixed(2)}</div>}
              <div className="text-xl font-bold text-slate-900">Total: ${Number(quote.total).toFixed(2)}</div>
            </div>
            <div className="mt-4">
              <Button variant="outline" size="sm" className="w-full sm:w-auto" onClick={() => window.open(`${API_URL}/api/custom-orders/${token}/pdf`, '_blank', 'noopener')} data-testid="custom-order-download-pdf">
                <Download className="h-4 w-4 mr-1" /> Download PDF
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6 space-y-5">
            <h2 className="font-heading font-semibold text-slate-900">Reserve your order</h2>

            <div>
              <Label className="text-xs">Payment method</Label>
              <div className="flex gap-3 mt-1">
                {opts.zelle_enabled && (
                  <button type="button" onClick={() => setMethod('zelle')} className={`flex-1 border rounded-lg py-2 text-sm font-medium ${method === 'zelle' ? 'border-teal-600 bg-teal-50 text-teal-800' : 'border-slate-200 text-slate-600'}`} data-testid="custom-pay-zelle">Zelle</button>
                )}
                {opts.cashapp_enabled && (
                  <button type="button" onClick={() => setMethod('cash_app')} className={`flex-1 border rounded-lg py-2 text-sm font-medium ${method === 'cash_app' ? 'border-teal-600 bg-teal-50 text-teal-800' : 'border-slate-200 text-slate-600'}`} data-testid="custom-pay-cashapp">Cash App</button>
                )}
              </div>
              {!opts.zelle_enabled && !opts.cashapp_enabled && <p className="text-sm text-red-600 mt-1">No payment methods are currently available. Please contact support.</p>}
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div><Label className="text-xs">Full name *</Label><Input value={addr.name} onChange={(e) => setAddr({ ...addr, name: e.target.value })} data-testid="custom-addr-name" /></div>
              <div><Label className="text-xs">Phone *</Label><Input value={phone} onChange={(e) => setPhone(e.target.value)} data-testid="custom-addr-phone" /></div>
              <div className="sm:col-span-2"><Label className="text-xs">Address line 1 *</Label><Input value={addr.address_line1} onChange={(e) => setAddr({ ...addr, address_line1: e.target.value })} data-testid="custom-addr-line1" /></div>
              <div className="sm:col-span-2"><Label className="text-xs">Address line 2</Label><Input value={addr.address_line2} onChange={(e) => setAddr({ ...addr, address_line2: e.target.value })} /></div>
              <div><Label className="text-xs">City *</Label><Input value={addr.city} onChange={(e) => setAddr({ ...addr, city: e.target.value })} data-testid="custom-addr-city" /></div>
              <div><Label className="text-xs">State *</Label><Input value={addr.state} onChange={(e) => setAddr({ ...addr, state: e.target.value })} data-testid="custom-addr-state" /></div>
              <div><Label className="text-xs">ZIP *</Label><Input value={addr.zip_code} onChange={(e) => setAddr({ ...addr, zip_code: e.target.value })} data-testid="custom-addr-zip" /></div>
            </div>

            {method === 'zelle' && (
              <div className="grid sm:grid-cols-2 gap-4 border-t pt-4">
                <div className="sm:col-span-2 text-xs text-slate-500">Tell us the name & phone/email tied to your Zelle so we can match your payment.</div>
                <div><Label className="text-xs">Zelle name</Label><Input value={zelleContact.name} onChange={(e) => setZelleContact({ ...zelleContact, name: e.target.value })} data-testid="custom-zelle-name" /></div>
                <div><Label className="text-xs">Zelle phone / email</Label><Input value={zelleContact.handle} onChange={(e) => setZelleContact({ ...zelleContact, handle: e.target.value })} data-testid="custom-zelle-handle" /></div>
              </div>
            )}

            <Button className="w-full bg-teal-700 hover:bg-teal-800 text-white" onClick={submit} disabled={submitting || (!opts.zelle_enabled && !opts.cashapp_enabled)} data-testid="custom-order-reserve-btn">
              {submitting ? 'Reserving…' : `Reserve — $${Number(quote.total).toFixed(2)}`}
            </Button>
            <p className="text-[11px] text-slate-400 text-center flex items-center justify-center gap-1"><CheckCircle2 className="h-3 w-3" /> Payment details shown after you reserve. All products are for research use only (RUO).</p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default CustomOrderPage;
